import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Modal,
  Paper,
  Progress,
  Stack,
  Text,
} from '@mantine/core';
import { FaCircle, FaRedo, FaStop, FaVideo } from 'react-icons/fa';
import useVideoRecorder from '@hooks/useVideoRecorder';
import {
  MAX_VIDEO_DURATION_SECONDS,
  recordedBlobToFile,
} from '@utils/videoFile';
import { getUploadUrl } from '@services/api/fileManagementApi';
import { saveWorkerPersonalVideo } from '@services/api/workerPersonalVideoApi';
import { FolderDesignationEnum } from '@th-types/uploadImage.type';
import { extractErrorMessage } from '@worker/helpers/my-jobs-helpers';

interface VideoIntroModalProps {
  opened: boolean;
  onClose: () => void;
  workerId: number;
  onSaved: (personalVideoId: number) => void;
}

const PROMPT_TEXT =
  'Why do you like marketing events? Why should companies hire you?';

const LIVE_VIDEO_STYLE: React.CSSProperties = {
  width: '100%',
  maxHeight: 300,
  objectFit: 'cover',
  transform: 'scaleX(-1)',
};

const RECORDED_VIDEO_STYLE: React.CSSProperties = {
  width: '100%',
  maxHeight: 300,
  borderRadius: '8px',
  objectFit: 'contain',
  display: 'block',
};

/**
 * Format seconds as mm:ss with zero-padding (e.g. 0 -> '00:00', 30 -> '00:30').
 */
const formatTime = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * "Show Your Personality!" modal for recording or uploading a worker's
 * 30-second personal video intro.
 */
function VideoIntroModal({
  opened,
  onClose,
  workerId,
  onSaved,
}: VideoIntroModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    state,
    error: recorderError,
    recordedBlob,
    recordedUrl,
    elapsedSeconds,
    maxDurationSeconds,
    videoRef,
    startCamera,
    startRecording,
    stopRecording,
    retake,
    enterRecordingMode,
    exitRecordingMode,
  } = useVideoRecorder({ maxDurationSeconds: MAX_VIDEO_DURATION_SECONDS });

  useEffect(() => {
    if (opened) {
      enterRecordingMode();
      startCamera();
    }
    return () => exitRecordingMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  const uploadAndSave = async (file: File) => {
    setError(null);
    setLoading(true);
    try {
      const contentType = file.type.split(';')[0].trim();
      const { s3FileId, preSignedUrl } = await getUploadUrl({
        designation: FolderDesignationEnum.PERSON_PERSONAL_VIDEO,
        fileName: file.name,
        contentType,
        contentLength: file.size,
        entityId: String(workerId),
      });
      await axios.put(preSignedUrl, file, {
        headers: {
          'Content-Type': contentType,
          'x-amz-server-side-encryption': 'AES256',
        },
      });
      const { personalVideoId } = await saveWorkerPersonalVideo({ s3FileId });
      onSaved(personalVideoId);
      handleClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'Upload failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!recordedBlob) return;
    uploadAndSave(recordedBlobToFile(recordedBlob, 'video-intro'));
  };

  const handleRetake = async () => {
    setError(null);
    retake();
    await startCamera();
  };

  const timerLabel = `${formatTime(elapsedSeconds)} / ${formatTime(maxDurationSeconds)}`;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Show Your Personality!"
      size="lg"
      centered
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      zIndex={3001}
    >
      <Stack gap="md">
        <Text c="dimmed" size="sm">
          {PROMPT_TEXT}
        </Text>

        <Group gap="xs">
          <FaVideo size={18} />
          <Text fw={700}>30 Second Video Intro</Text>
        </Group>

        {error && (
          <Alert color="red" title="Error" data-testid="video-intro-error">
            {error}
          </Alert>
        )}

        {state === 'requesting-permission' && (
          <Paper p="xl" withBorder>
            <Center>
              <Text>Requesting camera access...</Text>
            </Center>
          </Paper>
        )}

        {state === 'ready' && (
          <Stack gap="sm">
            <Paper p="sm" withBorder>
              <Box
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={LIVE_VIDEO_STYLE}
                />
              </Box>
            </Paper>
            <Text size="sm" c="dimmed" ta="center">
              {timerLabel}
            </Text>
            <Group justify="center">
              <Button
                variant="filled"
                color="red"
                leftSection={<FaCircle size={12} />}
                onClick={startRecording}
              >
                Start Recording
              </Button>
            </Group>
          </Stack>
        )}

        {state === 'recording' && (
          <Stack gap="sm">
            <Paper p="sm" withBorder>
              <Box
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={LIVE_VIDEO_STYLE}
                />
                <Box
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(0,0,0,0.6)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                >
                  <FaCircle size={10} color="#ff4444" />
                  <Text size="sm" c="white" fw={500}>
                    REC
                  </Text>
                </Box>
              </Box>
            </Paper>
            <Text size="sm" c="dimmed" ta="center">
              {timerLabel}
            </Text>
            <Progress
              value={(elapsedSeconds / maxDurationSeconds) * 100}
              color="red"
            />
            <Group justify="center">
              <Button
                variant="filled"
                color="red"
                leftSection={<FaStop size={14} />}
                onClick={stopRecording}
              >
                Stop Recording
              </Button>
            </Group>
          </Stack>
        )}

        {state === 'stopped' && recordedUrl && (
          <Stack gap="sm">
            <Paper p="sm" withBorder>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Preview only, no captions needed */}
              <video
                src={recordedUrl}
                controls
                playsInline
                style={RECORDED_VIDEO_STYLE}
              />
            </Paper>
            <Group justify="center">
              <Button
                variant="default"
                leftSection={<FaRedo size={14} />}
                onClick={handleRetake}
              >
                Retake
              </Button>
            </Group>
          </Stack>
        )}

        {state === 'error' && (
          <Stack gap="sm">
            <Alert color="red" title="Camera Error">
              {recorderError}
            </Alert>
            <Group justify="center">
              <Button variant="outline" onClick={startCamera}>
                Try Again
              </Button>
            </Group>
          </Stack>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={state !== 'stopped' || !recordedBlob}
            loading={loading}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default VideoIntroModal;
